import { useCallback, useEffect, useState } from 'react'
import Router from 'next/router'
import { publicConfig } from 'config'
import { memberCardApiPath } from 'lib/community/model'
import { HandlePrefetcher, browserPrefetchEnv } from 'lib/community/prefetch'
import type { CommunityMemberCardResponse } from 'lib/community/types'

export type MemberCardState = 'idle' | 'loading' | 'ready' | 'missing' | 'error'

export type MemberCardAnswer =
  | { state: 'ready'; data: CommunityMemberCardResponse }
  | { state: 'missing' | 'error' }

/** One request per handle per page load, however many author lines name the member. A failed
 *  read is forgotten so a later hover can try again; a 404 is kept (the member has no page). */
const requests = new Map<string, Promise<MemberCardAnswer>>()
/** The same answers once settled, readable during render: a card opened on a prefetched (or
 *  warmed) handle paints its figures on the first frame instead of a skeleton. */
const settled = new Map<string, MemberCardAnswer>()

async function ask(key: string): Promise<MemberCardAnswer> {
  try {
    const res = await fetch(memberCardApiPath(key, Boolean(publicConfig('useTrailingSlash'))), {
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    })
    if (res.status === 404) {
      settled.set(key, { state: 'missing' })
      return { state: 'missing' }
    }
    if (!res.ok) {
      requests.delete(key)
      return { state: 'error' }
    }
    const answer: MemberCardAnswer = {
      state: 'ready',
      data: (await res.json()) as CommunityMemberCardResponse,
    }
    settled.set(key, answer)
    return answer
  } catch {
    requests.delete(key)
    return { state: 'error' }
  }
}

function load(handle: string): Promise<MemberCardAnswer> {
  const key = handle.toLowerCase()
  const known = requests.get(key)
  if (known) {
    return known
  }
  const request = ask(key)
  requests.set(key, request)
  return request
}

/** True when `handle` is answered or in flight — the prefetcher skips it (no budget spent). */
export function hasMemberCard(handle: string): boolean {
  return requests.has(handle.toLowerCase())
}

/** The settled answer for `handle`, if any — synchronous, for the first render after an open. */
export function peekMemberCard(handle: string): MemberCardAnswer | null {
  return settled.get(handle.toLowerCase()) ?? null
}

/**
 * The page's one early-load prefetcher (Amendment C.3): every `MemberCardTrigger` registers its
 * element; after load + idle the authors in or near the viewport are read into THIS cache (≤ 12
 * per page view, 2 in flight), so the card above answers from it. A client-side navigation is a
 * new page view (Next's `routeChangeComplete`).
 */
export const memberCardPrefetcher = new HandlePrefetcher({ load, has: hasMemberCard }, () =>
  browserPrefetchEnv(fn => {
    Router.events.on('routeChangeComplete', fn)
    return () => Router.events.off('routeChangeComplete', fn)
  })
)

/** Test seam: forget every cached answer. */
export function forgetMemberCards(): void {
  requests.clear()
  settled.clear()
}

/** Start (or join) the read for `handle` into the page cache — the prefetcher's and tests' seam. */
export function loadMemberCard(handle: string): Promise<MemberCardAnswer> {
  return load(handle)
}

/**
 * The hover card's data for `handle`: nothing is fetched on render, so sweeping the pointer down
 * a feed costs nothing. `warm()` starts the read early — the pointer has rested on the trigger
 * for the warm delay, before the card opens — into the page cache only; `request()` (the card
 * opening — hover intent or focus) is what the card's state follows, and it answers from the
 * cache when the warm-up or the page's idle prefetch (`memberCardPrefetcher`) got there first —
 * synchronously once settled (`peekMemberCard`), so a prefetched card never shows its skeleton.
 * Public, edge-cached data; nothing reader-specific.
 */
export function useMemberCard(handle: string): {
  state: MemberCardState
  data: CommunityMemberCardResponse | null
  request: () => void
  warm: () => void
} {
  // How many times the card has opened: 0 = never (idle). Each open re-asks `load`, which
  // answers from the page cache unless the last try failed.
  const [asked, setAsked] = useState(0)
  const [answer, setAnswer] = useState<MemberCardAnswer | null>(null)

  useEffect(() => {
    if (asked === 0) {
      return undefined
    }
    let live = true
    const run = async (): Promise<void> => {
      const a = await load(handle)
      if (live) {
        setAnswer(a)
      }
    }
    void run()
    return () => {
      live = false
    }
  }, [asked, handle])

  const request = useCallback(() => setAsked(n => n + 1), [])
  const warm = useCallback(() => {
    void load(handle)
  }, [handle])

  if (asked === 0) {
    return { state: 'idle', data: null, request, warm }
  }
  const shown = answer ?? peekMemberCard(handle)
  if (!shown) {
    return { state: 'loading', data: null, request, warm }
  }
  return shown.state === 'ready'
    ? { state: 'ready', data: shown.data, request, warm }
    : { state: shown.state, data: null, request, warm }
}
